'use client'

import { useSyncExternalStore } from 'react'
import { UTM_PARAMETERS } from '@nwwp/utils'
import { isClientSide } from '../utils/is-client-side'
import { getAnonymousId } from '../utils/local-storage'

const appendParamsToUrl = (baseUrl: string, params: Record<string, string>): string => {
  try {
    const isRelativeUrl = baseUrl.startsWith('/')
    const url = new URL(baseUrl, window.location.origin)
    const originalParams = new URLSearchParams(url.search)
    url.search = ''
    for (const [key, value] of Object.entries(params)) {
      url.searchParams.set(key, value)
    }
    for (const [key, value] of originalParams) {
      if (!url.searchParams.has(key)) {
        url.searchParams.set(key, value)
      }
    }
    return isRelativeUrl ? `${url.pathname}${url.search}${url.hash}` : url.toString()
  } catch {
    return baseUrl
  }
}

const emptySubscribe = () => () => {}

interface TrackingUrlOptions {
  appendAnonymousId?: boolean
  appendAnonymousIdKey?: string
}

const getTrackingUrl = (baseUrl: string, options: TrackingUrlOptions = {}): string => {
  if (!isClientSide()) {
    return baseUrl
  }

  const { appendAnonymousId = false, appendAnonymousIdKey = 'ajs_anonymous_id' } = options
  const params: Record<string, string> = {}

  const searchParams = new URLSearchParams(window.location.search)
  let hasUrlUtms = false

  for (const param of UTM_PARAMETERS) {
    const value = searchParams.get(param)
    if (value) {
      params[param] = value
      hasUrlUtms = true
    }
  }

  if (!hasUrlUtms) {
    try {
      const stored = sessionStorage.getItem('funnel_storage')
      if (stored) {
        const parsed = JSON.parse(stored) as { state: { tracking: Record<string, string> } }
        for (const param of UTM_PARAMETERS) {
          const value = parsed.state?.tracking?.[param]
          if (value) params[param] = value
        }
      }
    } catch {
      // sessionStorage unavailable or malformed JSON — continue
    }
  }

  if (appendAnonymousId) {
    const anonymousId = getAnonymousId()
    if (anonymousId) {
      params[appendAnonymousIdKey] = anonymousId
    }
  }

  if (Object.keys(params).length > 0) {
    return appendParamsToUrl(baseUrl, params)
  }

  return baseUrl
}

export const useTrackingUrl = (baseUrl: string, options: TrackingUrlOptions = {}): string => {
  // useSyncExternalStore handles SSR/hydration correctly: the server snapshot returns
  // baseUrl (no window access), and the client snapshot computes the tracking URL.
  // This avoids a useEffect→setState double-render while preserving hydration safety.
  return useSyncExternalStore(
    emptySubscribe,
    () => getTrackingUrl(baseUrl, options),
    () => baseUrl
  )
}
